"use client";

import { type Breakpoint, useBreakpoint, useBreakpointListener } from "@dc/hooks";
import { type Dispatch, type SetStateAction, useCallback, useRef, useState } from "react";

export type FiltersPanelCloseReason = "apply" | "cancel" | "dismiss";

export interface UseFiltersPanelControllerOptions<TSnapshot = unknown> {
  /**
   * Breakpoint that determines desktop vs. mobile rendering.
   * Defaults to `"sm"` to match the existing FiltersPanel usage.
   */
  breakpoint?: Breakpoint;
  /**
   * Capture any state that should be restored if the panel closes without applying.
   */
  captureSnapshot?: () => TSnapshot;
  /**
   * Restore the snapshot captured during the current open session when the panel closes without applying.
   */
  restoreSnapshot?: (snapshot: TSnapshot | undefined) => void;
  /**
   * Called whenever the panel transitions to an open state.
   */
  onOpen?: () => void;
  /**
   * Called after the panel finishes closing. Receives the reason that triggered the close.
   */
  onClose?: (reason: FiltersPanelCloseReason) => void;
  /**
   * Called right before the panel closes due to an explicit cancel action.
   */
  onCancel?: () => void;
  /**
   * Called right before the panel closes because the user dismissed it (e.g. clicking outside).
   */
  onDismiss?: () => void;
  /**
   * Called before the panel closes as part of applying changes.
   */
  onApply?: () => void;
  /**
   * Called when a consumer-driven reset action occurs while the panel is open.
   */
  onReset?: () => void;
}

export interface FiltersPanelControllerState {
  isOpen: boolean;
  isDesktop: boolean;
  isMobile: boolean;
  filterSearchValue: string;
}

export interface FiltersPanelControllerHandlers {
  open: () => void;
  close: (reason?: FiltersPanelCloseReason) => void;
  toggle: () => void;
  apply: () => void;
  cancel: () => void;
  reset: () => void;
  handleOpenChange: (nextOpen: boolean) => void;
  handleTriggerPress: () => void;
  setFilterSearchValue: Dispatch<SetStateAction<string>>;
}

export type FiltersPanelControllerReturn = FiltersPanelControllerState & FiltersPanelControllerHandlers;

/**
 * NOTE: may be worth revisiting this hook in the future to deobfuscate things.
 * There is a lot of back and forth between this hook and the consumer
 */
export const useFiltersPanelController = <TSnapshot = unknown>(
  options: UseFiltersPanelControllerOptions<TSnapshot> = {},
): FiltersPanelControllerReturn => {
  const {
    breakpoint = "sm",
    captureSnapshot,
    restoreSnapshot,
    onOpen,
    onClose,
    onCancel,
    onDismiss,
    onApply,
    onReset,
  } = options;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filterSearchValue, setFilterSearchValue] = useState<string>("");

  const isOpenRef = useRef<boolean>(isOpen);
  const snapshotRef = useRef<TSnapshot | undefined>(undefined);
  const closingReasonRef = useRef<FiltersPanelCloseReason>("dismiss");

  const breakpointMatches = useBreakpoint(breakpoint);
  const isDesktop = breakpointMatches === undefined ? true : breakpointMatches;
  const isMobile = !isDesktop;

  isOpenRef.current = isOpen;

  const dispatchOpenChange = useCallback(
    (nextOpen: boolean, closeReason: FiltersPanelCloseReason = "dismiss") => {
      let didOpen = false;
      let didClose = false;

      setIsOpen((prev) => {
        if (prev === nextOpen) {
          return prev;
        }

        if (nextOpen) {
          didOpen = true;
          return true;
        }

        didClose = true;
        return false;
      });

      if (didOpen) {
        snapshotRef.current = captureSnapshot?.();
        closingReasonRef.current = "dismiss";
        setFilterSearchValue("");
        onOpen?.();
        return;
      }

      if (!didClose) {
        return;
      }

      closingReasonRef.current = closeReason;

      if (closeReason === "cancel") {
        onCancel?.();
      } else if (closeReason === "dismiss") {
        onDismiss?.();
      } else if (closeReason === "apply") {
        onApply?.();
      }

      if (closeReason !== "apply") {
        restoreSnapshot?.(snapshotRef.current);
      }

      onClose?.(closeReason);
      snapshotRef.current = undefined;
    },
    [captureSnapshot, onApply, onCancel, onClose, onDismiss, onOpen, restoreSnapshot],
  );

  const open = useCallback(() => {
    dispatchOpenChange(true);
  }, [dispatchOpenChange]);

  const close = useCallback(
    (reason: FiltersPanelCloseReason = "dismiss") => {
      dispatchOpenChange(false, reason);
    },
    [dispatchOpenChange],
  );

  const apply = useCallback(() => {
    close("apply");
  }, [close]);

  const cancel = useCallback(() => {
    close("cancel");
  }, [close]);

  const toggle = useCallback(() => {
    if (isOpen) {
      cancel();
    } else {
      open();
    }
  }, [cancel, isOpen, open]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        dispatchOpenChange(true);
      } else {
        dispatchOpenChange(false, closingReasonRef.current ?? "dismiss");
      }
    },
    [dispatchOpenChange],
  );

  const handleTriggerPress = useCallback(() => {
    toggle();
  }, [toggle]);

  const reset = useCallback(() => {
    onReset?.();
  }, [onReset]);

  useBreakpointListener(breakpoint, (isAtOrAboveDesktop) => {
    if (!isAtOrAboveDesktop) return;

    dispatchOpenChange(false, "dismiss");
  });

  return {
    isOpen,
    isDesktop,
    isMobile,
    filterSearchValue,
    setFilterSearchValue,
    open,
    close,
    toggle,
    apply,
    cancel,
    reset,
    handleOpenChange,
    handleTriggerPress,
  };
};
